import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * טוען נתוני צ'אט (רשימת שיחות) ברקע כל 5 דקות.
 * רץ מ-Layout כדי שה-cache יהיה מוכן כשהמשתמש נכנס לצ'אט.
 */
export function useChatPrefetch({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { isLoading } = useAuth();
  const { effectiveUserId } = useImpersonation();

  useEffect(() => {
    if (!effectiveUserId || isLoading || !enabled) return;

    const prefetch = async () => {
      try {
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: ['chat-conversations', effectiveUserId],
            queryFn: async () => {
              // Filter conversations through the user's own participations so
              // we don't leak other users' conversations into the cache.
              const { data: participations } = await supabase
                .from('conversation_participants')
                .select('conversation_id')
                .eq('user_id', effectiveUserId)
                .is('left_at', null);

              const ids = (participations || []).map((p: any) => p.conversation_id);
              if (ids.length === 0) return [];

              const { data, error } = await supabase
                .from('conversations')
                .select(`
                  id,
                  type,
                  challenge_id,
                  last_message_at,
                  last_message_text,
                  last_message_sender_id
                `)
                .in('id', ids)
                .eq('type', 'direct');

              if (error) throw error;
              return data;
            },
            staleTime: 5 * 60 * 1000,
          }),
          queryClient.prefetchQuery({
            queryKey: ['chat-user-status', effectiveUserId],
            queryFn: async () => {
              const { data: enrollment, error } = await supabase
                .from('user_challenge_enrollments')
                .select(`
                  challenge_id,
                  challenges!inner(challenge_type, status)
                `)
                .eq('user_id', effectiveUserId)
                .is('completed_at', null)
                .maybeSingle();

              if (error) throw error;
              return enrollment;
            },
            staleTime: 5 * 60 * 1000,
          }),
        ]);
      } catch (error) {
        console.debug('[ChatPrefetch] Error:', error);
      }
    };

    // טעינה ראשונית מיידית (Auth כבר מוכן)
    prefetch();
    // רענון כל 5 דקות
    const interval = setInterval(prefetch, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [effectiveUserId, isLoading, enabled, queryClient]);
}
